import {
  FileCreateRequest,
  FileDto,
  FileUploadLinkResponse,
} from '@act/shared/data-transfer-objects';
import { filesRequests } from '../requests/files.requests';
import { makeRequest } from './api-request';

type JSFile = File;

export interface UploadProgressCallback {
  (progress: number): void;
}

export interface UploadFilesOptions {
  onProgress?: UploadProgressCallback;
  abortController?: AbortController;
}

export const uploadFiles = async (
  files: JSFile[],
  options?: UploadFilesOptions | UploadProgressCallback
): Promise<FileDto[]> => {
  // Handle backward compatibility
  const onProgress =
    typeof options === 'function' ? options : options?.onProgress;
  const abortController =
    typeof options === 'function' ? undefined : options?.abortController;

  if (onProgress) {
    // Upload files sequentially with progress tracking
    const results: FileDto[] = [];
    let totalProgress = 0;

    for (let i = 0; i < files.length; i++) {
      // Check if upload was aborted
      if (abortController?.signal.aborted) {
        throw new Error('Upload aborted');
      }

      const file = files[i];
      const fileProgress = await uploadFileWithProgress(
        file,
        fileProgress => {
          // Check if upload was aborted during progress
          if (abortController?.signal.aborted) {
            throw new Error('Upload aborted');
          }
          // Calculate overall progress: previous files + current file progress
          const overallProgress =
            (i / files.length) * 100 + fileProgress / files.length;
          onProgress(overallProgress);
        },
        abortController
      );
      results.push(fileProgress);
      totalProgress = ((i + 1) / files.length) * 100;
      onProgress(totalProgress);
    }
    return results;
  } else {
    // Fallback to parallel uploads for backward compatibility
    return Promise.all(files.map(file => uploadFile(file)));
  }
};

export const uploadFile = async (file: JSFile): Promise<FileDto> => {
  const presignedLinkResponse = await getFileUploadLink(file);
  try {
    await uploadFileToPresignedS3Link(file, presignedLinkResponse);
    return presignedLinkResponse.file;
  } catch (e) {
    await cleanupFileAfterUploadFailure(presignedLinkResponse);
    throw e;
  }
};

const uploadFileWithProgress = async (
  file: JSFile,
  onProgress?: (progress: number) => void,
  abortController?: AbortController
): Promise<FileDto> => {
  const presignedLinkResponse = await getFileUploadLink(file);
  try {
    await uploadFileToPresignedS3LinkWithProgress(
      file,
      presignedLinkResponse,
      onProgress,
      abortController
    );
    return presignedLinkResponse.file;
  } catch (e) {
    await cleanupFileAfterUploadFailure(presignedLinkResponse);
    throw e;
  }
};

const getFileUploadLink = async (
  file: JSFile
): Promise<FileUploadLinkResponse> => {
  const request: FileCreateRequest = {
    fileName: file.name,
    mimeType: file.type,
    size: file.size,
  };

  return makeRequest(filesRequests.getFileUploadLink, {
    body: request,
    params: null,
  });
};

const uploadFileToPresignedS3Link = async (
  file: JSFile,
  presignedLinkResponse: FileUploadLinkResponse
): Promise<any> => {
  await fetch(presignedLinkResponse.url, {
    method: 'PUT',
    body: file,
  });
};

const uploadFileToPresignedS3LinkWithProgress = async (
  file: JSFile,
  presignedLinkResponse: FileUploadLinkResponse,
  onProgress?: (progress: number) => void,
  abortController?: AbortController
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    // Set up abort handling
    if (abortController) {
      abortController.signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    xhr.upload.addEventListener('progress', event => {
      // Check if upload was aborted during progress
      if (abortController?.signal.aborted) {
        xhr.abort();
        return;
      }

      if (event.lengthComputable && onProgress) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr.response);
      } else {
        reject(new Error(`Upload failed with status: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('PUT', presignedLinkResponse.url);
    xhr.send(file);
  });
};

const cleanupFileAfterUploadFailure = (
  presignedLinkResponse: FileUploadLinkResponse
): Promise<any> => {
  // If there is an error uploading the file to S3 then clean up the file record created

  return makeRequest(filesRequests.cleanUpErroneousFile, {
    body: null,
    params: { id: presignedLinkResponse.file.id },
  });
};
